import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Api from "../services/apis/setup";

export const initialForm = {
  registrationNumber: "",
  chasisNumber: '',
  engineNumber: "",
  odo: '',
  lastService: "",
  nextServiceDue: "",
  nextOilChangeOdo: '',
  nextGreasingOdo: '',
  remarks: "",
  transportName: "",
  owner: "",
  address: "",
  contact: "",
};

type Props = {}

function AddNewVehicle({ }: Props) {
  const [form, setForm] = useState(initialForm);
  const [addVehicleApi] = Api.useAddVehicleMutation();
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const vehicle = {
      number: form.registrationNumber,
      odo: Number(form.odo),
      lastService: form.lastService,
      nextServiceDue: form.nextServiceDue,
      nextOilChangeOdo: Number(form.nextOilChangeOdo),
      nextGreasingOdo: Number(form.nextGreasingOdo),
      remarks: form.remarks,
      transport: {
        registrationNumber: form.registrationNumber,
        chasisNumber: form.chasisNumber,
        engineNumber: form.engineNumber,
        transportName: form.transportName,
        details: {
          owner: form.owner,
          address: form.address,
          contact: form.contact,
        },
      },
      history: [],
    };
    const response = await addVehicleApi({
      baseURL: "/vehicles",
      method: "POST",
      payload: JSON.stringify(vehicle),
    });
    if (response && 'data' in response) {
      setForm(initialForm);
    }
    if ('error' in response) {
      console.error("Error adding vehicle:", response.error);
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Add New Vehicle
      </Typography>
      <Box sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Vehicle Identification Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Vehicle Identification
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Vehicle Registration Number"
                  name="registrationNumber"
                  value={form.registrationNumber}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Chasis Number"
                  name="chasisNumber"
                  value={form.chasisNumber}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Engine Number"
                  name="engineNumber"
                  value={form.engineNumber}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Transport Information Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Transport Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Transport Name"
                  name="transportName"
                  value={form.transportName}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Owner"
                  name="owner"
                  value={form.owner}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Contact"
                  name="contact"
                  value={form.contact}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Service Information Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Service Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Current ODO Reading"
                  name="odo"
                  value={form.odo}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputMode: 'numeric'
                  }}
                  inputProps={{
                    pattern: '[0-9]*',
                    min: 0
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Last Service Date"
                  value={form.lastService ? new Date(form.lastService) : null}
                  onChange={(date) => setForm({...form, lastService: date ? date.toISOString().split('T')[0] : ''})}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputBase-input': {
                          '&:focus': {
                            outline: 'none',
                          },
                        },
                      }
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Next Service Due"
                  value={form.nextServiceDue ? new Date(form.nextServiceDue) : null}
                  onChange={(date) => setForm({...form, nextServiceDue: date ? date.toISOString().split('T')[0] : ''})}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1,
                          },
                        },
                        '& .MuiInputBase-input': {
                          '&:focus': {
                            outline: 'none',
                          },
                        },
                      }
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Next Oil Change ODO"
                  name="nextOilChangeOdo"
                  value={form.nextOilChangeOdo}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputMode: 'numeric'
                  }}
                  inputProps={{
                    pattern: '[0-9]*',
                    min: 0
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Next Greasing ODO"
                  name="nextGreasingOdo"
                  value={form.nextGreasingOdo}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputMode: 'numeric'
                  }}
                  inputProps={{
                    pattern: '[0-9]*',
                    min: 0
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Additional Information Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Additional Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Remarks"
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 120,
                  backgroundColor: 'var(--primary-color)',
                  '&:hover': {
                    backgroundColor: 'var(--primary-dark)'
                  }
                }}
              >
                Add Vehicle
              </Button>
            </Box>
          </form>
          </LocalizationProvider>
        </Paper>
      </Box>
    </Box>
  );
}

export default AddNewVehicle;